import type {
  PartnerCode,
  PartnerCodeMember,
  PartnerDashboardCode,
  PublicPartnerCode,
  PartnerSignupResult,
  PartnerClaimResponse,
} from "@atlas/shared";
import { apiGet, apiPost, apiPatch, apiDelete } from "../lib/api-client";

export type { PartnerCode, PartnerCodeMember, PartnerDashboardCode, PublicPartnerCode, PartnerSignupResult };

// Partner codes (ATL-134): /with/{code} links (and the old /beta/{code}).

/** localStorage key holding the code between the landing page and signup. */
export const PARTNER_STORAGE_KEY = "atlas-partner";
/** Set when the stored code came from a ?with= param (ads), not the landing page. */
const PARTNER_SILENT_KEY = "atlas-partner-silent";

export function readStoredPartnerCode(): string | null {
  try {
    return window.localStorage.getItem(PARTNER_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storePartnerCode(code: string, opts: { silent?: boolean } = {}): void {
  try {
    window.localStorage.setItem(PARTNER_STORAGE_KEY, code);
    if (opts.silent) window.localStorage.setItem(PARTNER_SILENT_KEY, "1");
    else window.localStorage.removeItem(PARTNER_SILENT_KEY);
  } catch {
    // Private mode or blocked storage: the ?invite= search param and the
    // HttpOnly cookie the API set still carry it.
  }
}

export function clearStoredPartnerCode(): void {
  try {
    window.localStorage.removeItem(PARTNER_STORAGE_KEY);
    window.localStorage.removeItem(PARTNER_SILENT_KEY);
  } catch {
    // ignore
  }
}

/** True when the stored code arrived silently, so signup shows no partner banner. */
export function storedPartnerCodeIsSilent(): boolean {
  try {
    return window.localStorage.getItem(PARTNER_SILENT_KEY) === "1";
  } catch {
    return false;
  }
}

export function fetchPartnerCode(code: string): Promise<{ partner: PublicPartnerCode }> {
  return apiGet(`/with/${encodeURIComponent(code)}`);
}

export function claimPartnerCode(code: string): Promise<PartnerClaimResponse> {
  return apiPost(`/with/${encodeURIComponent(code)}/claim`);
}

export interface PartnerCodeInput {
  label: string;
  code?: string;
  landingLine?: string;
  notes?: string;
  maxSeats: number | null;
  durationDays: number | null;
  /** Spec 30 perk: signups through the link get Full history. */
  grantsFullHistory: boolean;
  /** Waypoints each signup gets on their first synced walk; null = none. */
  rewardWaypoints: number | null;
}

export function fetchAdminPartnerCodes(): Promise<{ codes: PartnerCode[] }> {
  return apiGet("/admin/partners");
}

export function createPartnerCode(data: PartnerCodeInput): Promise<{ code: PartnerCode }> {
  return apiPost("/admin/partners", data);
}

export type PartnerCodePatch = {
  label?: string;
  landingLine?: string | null;
  notes?: string | null;
  maxSeats?: number | null;
  durationDays?: number | null;
  grantsFullHistory?: boolean;
  rewardWaypoints?: number | null;
  enabled?: boolean;
  /** The partner's Atlas account by email or username; "" or null clears it. */
  owner?: string | null;
};

export function updatePartnerCode(id: string, data: PartnerCodePatch): Promise<{ code: PartnerCode }> {
  return apiPatch(`/admin/partners/${id}`, data);
}

export function fetchPartnerCodeMembers(id: string): Promise<{ members: PartnerCodeMember[] }> {
  return apiGet(`/admin/partners/${id}/members`);
}

export function removePartnerCodeMember(
  id: string,
  userId: string,
): Promise<{ ok: boolean; downgraded: boolean }> {
  return apiDelete(`/admin/partners/${id}/members/${userId}`);
}

export function extendPartnerCodeMember(
  id: string,
  userId: string,
  days: number,
): Promise<{ ok: boolean; expiresAt: string }> {
  return apiPost(`/admin/partners/${id}/members/${userId}/extend`, { days });
}

/** The signed-in partner's own codes (empty for everyone else). */
export function fetchPartnerDashboard(): Promise<{ codes: PartnerDashboardCode[] }> {
  return apiGet("/partner");
}
